<template>
  <div class="card">
    <div
      class="card-body py-3 d-flex justify-content-between align-items-center"
    >
      <h2 class="h5 mb-0 titulo_pagina">Clientes</h2>

      <button
        class="btn btn-success d-inline-flex align-items-center gap-1"
        @click="onAdd"
      >
        <span class="material-symbols-rounded">add</span>
        Agregar
      </button>
    </div>

    <div class="card-body pt-0">
      <div v-if="loading" class="text-center py-5">
        <Loading mensaje="Cargando clientes…" />
      </div>

      <div
        v-else-if="clientes.length === 0"
        class="alert alert-info mb-0"
        role="alert"
      >
        <SinDatos mensaje="No hay clientes cargados por el momento." />
      </div>

      <div v-else class="table-responsive fo-table-wrap">
        <table class="table table-sm table-hover align-middle mb-0 fo-table">
          <thead>
            <tr>
              <th>Nombre</th>
              <th>Celular</th>
              <th v-if="usaEmailCliente">Email</th>
              <th>Tipo</th>
              <th v-if="usaEmailCliente">Aviso Email</th>
              <th>Cant. Reservas</th>
              <th class="fo-col-actions">Acciones</th>
            </tr>
          </thead>

          <tbody>
            <tr v-for="c in clientes" :key="c.id_cliente">
              <td>{{ c.nombre }}</td>
              <td>{{ c.celular }}</td>
              <td v-if="usaEmailCliente">{{ c.email }}</td>
              <td>{{ c.tipo_cliente_desc }}</td>
              <td v-if="usaEmailCliente" class="text-center">
                <div
                  class="form-check form-switch d-inline-flex justify-content-center"
                >
                  <input
                    class="form-check-input"
                    type="checkbox"
                    :checked="c.aviso_email"
                    disabled
                  />
                </div>
              </td>
              <td>{{ c.cant_reservas }}</td>

              <td class="text-center">
                  <button class="btn btn-outline-primary me-2" @click="onEdit(c)">
                    <span class="material-symbols-rounded">edit</span> Editar
                  </button>

                  <button class="btn btn-outline-danger" @click="onDelete(c)">
                    <span class="material-symbols-rounded">delete_forever</span>
                    Eliminar
                  </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- MODAL ALTA / EDICIÓN -->
    <BaseModal
      v-model="modalOpen"
      :title="form.id_cliente ? 'Editar cliente' : 'Agregar cliente'"
      panelClass="w-100 w-md-50"
      :closable="!saving"
      :loading="saving"
      okLabel="Guardar"
      cancelLabel="Cancelar"
      @confirm="onSave"
      @cancel="onCancel"
    >
      <form class="d-flex flex-column gap-3" @submit.prevent="onSave">
        <div class="row g-2">
          <div class="col-12">
            <label class="form-label mb-1">Nombre *</label>
            <input
              class="form-control"
              v-model.trim="form.nombre"
              :disabled="saving"
              autocomplete="off"
              placeholder="Ej: Juan Pérez"
            />
            <div v-if="errors.nombre" class="text-danger small mt-1">
              {{ errors.nombre }}
            </div>
          </div>

          <div class="col-12">
            <label class="form-label mb-1">Celular</label>
            <input
              class="form-control"
              v-model.trim="form.celular"
              :disabled="saving"
              autocomplete="off"
              placeholder="Ej: 1112345678"
            />
          </div>

          <div v-if="usaEmailCliente" class="col-12">
            <label class="form-label mb-1">Email</label>
            <input
              type="email"
              class="form-control"
              v-model.trim="form.email"
              :disabled="saving"
              autocomplete="off"
              placeholder="Ej: juan@mail.com"
            />
            <div v-if="errors.email" class="text-danger small mt-1">
              {{ errors.email }}
            </div>
          </div>

          <div class="col-12">
            <label class="form-label mb-1">Tipo cliente *</label>
            <select
              class="form-select form-select-sm"
              v-model="form.tipo_cliente"
            >
              <option v-for="t in tipos_cliente" :key="t.id" :value="t.id">
                {{ t.descripcion }}
              </option>
            </select>
            <div v-if="errors.tipo_cliente" class="text-danger small mt-1">
              {{ errors.tipo_cliente }}
            </div>
          </div>

          <div v-if="usaEmailCliente" class="col-12 d-flex align-items-end">
            <div class="form-check form-switch">
              <input
                class="form-check-input"
                type="checkbox"
                id="avisoEmail"
                v-model="form.aviso_email"
                :disabled="saving"
              />
              <label class="form-check-label" for="avisoEmail"
                >Aviso por email</label
              >
            </div>
          </div>
        </div>

        <div v-if="saveError" class="alert alert-danger mb-0" role="alert">
          {{ saveError }}
        </div>
      </form>
    </BaseModal>
  </div>

  <MostrarMensaje
    v-model:modelValue="mostrarMensajeError"
    status="error"
    :message="mensajeError"
    :duration="duracionMensajeError"
    :closable="true"
  />

  <MostrarMensaje
    v-model:modelValue="mostrarMensajeSuccess"
    status="success"
    :message="mensajeSuccess"
    :duration="duracionMensajeSuccess"
    :closable="true"
  />

  <MostrarMensaje
    v-model:modelValue="mostrarMensajeConfirmacion"
    status="info"
    mode="confirm"
    :duration="0"
    :closable="true"
    ok-label="Confirmar"
    cancel-label="Cancelar"
    message="Confirma dar de baja el cliente?"
    @confirm="borrarCliente"
    @cancel="onCancel"
  />
</template>

<script setup>
import { ref, reactive, onMounted } from "vue";
import Loading from "@/components/Loading.vue";
import SinDatos from "@/components/SinDatos.vue";
import BaseModal from "@/components/BaseModal.vue";
import MostrarMensaje from "@/components/MostrarMensaje.vue";
import { usaEmailCliente } from "@/config/features";

import {
  apiGetClientes,
  apiGetTipoCliente,
  apiPostCliente,
  apiPutCliente,
  apiDeleteCliente,
} from "@/api.js";

const loading = ref(true);
const clientes = ref([]);
const tipos_cliente = ref([]);

let controller = null;

const modalOpen = ref(false);
const saving = ref(false);
const saveError = ref("");

const form = reactive(resetForm());
const errors = reactive({ nombre: "", email: "", tipo_cliente: "" });

const mostrarMensajeError = ref(false);
const mensajeError = ref("");
const duracionMensajeError = ref(5000);

const mostrarMensajeSuccess = ref(false);
const mensajeSuccess = ref("");
const duracionMensajeSuccess = ref(5000);

const mostrarMensajeConfirmacion = ref(false);

const idClienteDelete = ref(-1);

onMounted(async () => {
  await cargarTiposCliente();
  await fetchClientes();
});

function resetForm() {
  return {
    id_cliente: null,
    nombre: "",
    celular: "",
    email: "",
    tipo_cliente: 1,
    aviso_email: false,
  };
}

function fillFormFromCliente(c) {
  form.id_cliente = c?.id_cliente ?? null;
  form.nombre = c?.nombre ?? "";
  form.celular = c?.celular ?? "";
  form.email = usaEmailCliente ? c?.email ?? "" : "";
  form.tipo_cliente = c?.tipo_cliente ?? "";
  form.aviso_email = usaEmailCliente ? !!c?.aviso_email : false;
}

function clearErrors() {
  errors.nombre = "";
  errors.email = "";
  errors.tipo_cliente = "";
  saveError.value = "";
}

function validate() {
  clearErrors();
  let ok = true;

  if (!form.nombre?.trim()) {
    errors.nombre = "El nombre es obligatorio.";
    ok = false;
  }
  if (!form.tipo_cliente) {
    errors.tipo_cliente = "El tipo de cliente es obligatorio.";
    ok = false;
  }
  if (usaEmailCliente && form.email?.trim()) {
    const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim());
    if (!emailOk) {
      errors.email = "El email no tiene un formato válido.";
      ok = false;
    }
  }
  return ok;
}

const getTipoClienteDesc = (idTipo) => {
  const t = tipos_cliente.value.find((t) => t.id === idTipo);
  return t ? t.descripcion : "";
};

async function fetchClientes() {
  loading.value = true;

  try {
    controller?.abort?.();
  } catch {}
  controller =
    typeof AbortController !== "undefined" ? new AbortController() : null;

  try {
    const data = await apiGetClientes({}, { signal: controller?.signal });
    const lista = Array.isArray(data)
      ? data
      : data?.results ?? data?.items ?? [];

    clientes.value = lista.map((x, i) => ({
      id_cliente: x.id_cliente ?? i,
      nombre: x.nombre ?? "",
      celular: x.celular ?? "",
      email: usaEmailCliente ? x.email ?? "" : "",
      tipo_cliente: x.tipo_cliente ?? "",
      tipo_cliente_desc: getTipoClienteDesc(x.tipo_cliente) ?? "",
      aviso_email: usaEmailCliente ? !!x.aviso_email : false,
      cant_reservas: x.cant_reservas ?? 0,
    }));
  } catch (e) {
    console.error(e);
    mensajeError.value =
      "No se pudieron cargar los clientes. Intente nuevamente por favor.";
    mostrarMensajeError.value = true;
  } finally {
    loading.value = false;
  }
}

const cargarTiposCliente = async () => {
  try {
    const data = await apiGetTipoCliente({ signal: controller?.signal });
    const lista = Array.isArray(data)
      ? data
      : data?.results ?? data?.items ?? [];

    tipos_cliente.value = lista.map((s, i) => ({
      id: s.id_tipo_cliente,
      descripcion: s.descripcion ?? "",
      alta_fecha: s.alta_fecha,
      alta_usuario: s.alta_usuario,
    }));
  } catch (err) {
    console.error("Error cargando tipos de cliente", err);
  }
};

function onAdd() {
  Object.assign(form, resetForm());
  clearErrors();
  modalOpen.value = true;
}

function onEdit(c) {
  fillFormFromCliente(c);
  clearErrors();
  modalOpen.value = true;
}

function onCancel() {
  modalOpen.value = false;
}

async function onSave() {
  if (!validate()) return;
  saving.value = true;
  saveError.value = "";

  const payload = {
    nombre: form.nombre.trim(),
    celular: form.celular?.trim() || null,
    email: usaEmailCliente ? form.email?.trim() || null : null,
    tipo_cliente: form.tipo_cliente,
    aviso_email: usaEmailCliente ? !!form.aviso_email : false,
  };

  try {
    if (form.id_cliente) {
      await apiPutCliente(form.id_cliente, payload, {
        signal: controller?.signal,
      });
    } else {
      await apiPostCliente(payload, { signal: controller?.signal });
    }

    modalOpen.value = false;
    await fetchClientes();
    mensajeSuccess.value = form.id_cliente
      ? "El cliente fue modificado correctamente"
      : "El cliente se creo correctamente";
    duracionMensajeSuccess.value = 5000;
    mostrarMensajeSuccess.value = true;
  } catch (e) {
    console.error(e);
    saveError.value =
      "No se pudo guardar el cliente. Intente nuevamente por favor.";
  } finally {
    saving.value = false;
  }
}

function onDelete(c) {
  idClienteDelete.value = c.id_cliente;
  mostrarMensajeConfirmacion.value = true;
}

async function borrarCliente() {
  try {
    const resp = await apiDeleteCliente(idClienteDelete.value, {
      signal: controller?.signal,
    });
    await fetchClientes();
    if (resp.id_cliente == -1) {
      mostrarMensajeError.value = true;
      mensajeError.value =
        "No se puede eliminar el cliente porque tiene reservas a futuro.";
      duracionMensajeError.value = 0;
    } else {
      mensajeSuccess.value = "El cliente fue dado de baja correctamente";
      duracionMensajeSuccess.value = 3000;
      mostrarMensajeSuccess.value = true;
    }
  } catch (e) {
    console.error(e);
    mostrarMensajeError.value = true;
    mensajeError.value =
      "No se pudo eliminar el cliente. Intente nuevamente por favor.";
    duracionMensajeError.value = 0;
  }
}
</script>
